import React, { useEffect } from 'react';
import toast, { Toast } from 'react-hot-toast';
import { Check, AlertCircle, Info, X, Loader2, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// 🎵 Enhanced Audio Engine for "Apple-like" Haptics
const playToastSound = (type: 'success' | 'error' | 'warning' | 'info') => {
    try {
        if (localStorage.getItem('soundEnabled') === 'false') return;

        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContext) return;

        const ctx = new AudioContext();
        const now = ctx.currentTime;

        // Master Compressor to prevent clipping
        const compressor = ctx.createDynamicsCompressor();
        compressor.threshold.setValueAtTime(-24, now);
        compressor.knee.setValueAtTime(30, now);
        compressor.ratio.setValueAtTime(12, now);
        compressor.attack.setValueAtTime(0.003, now);
        compressor.release.setValueAtTime(0.25, now);
        compressor.connect(ctx.destination);

        const createOsc = (freq: number, type: 'sine' | 'triangle' | 'square', startTime: number, duration: number, vol: number) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.type = type;
            osc.frequency.setValueAtTime(freq, startTime);

            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(vol, startTime + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

            osc.connect(gain);
            gain.connect(compressor);

            osc.start(startTime);
            osc.stop(startTime + duration + 0.1);
        };

        if (type === 'success') {
            // "Success Chord" - Major Triad with slight delay (Soft & Positive)
            // C5 (523.25), E5 (659.25), G5 (783.99)
            createOsc(523.25, 'sine', now, 0.4, 0.1);
            createOsc(659.25, 'sine', now + 0.05, 0.4, 0.08);
            createOsc(783.99, 'sine', now + 0.1, 0.6, 0.06);
        } else if (type === 'error') {
            // "Bonk" - Low frequency thud/triangle (Noticeable but not harsh)
            createOsc(150, 'triangle', now, 0.15, 0.15);
            createOsc(100, 'sine', now + 0.05, 0.2, 0.1);
        } else {
            // "Pop" - High pitched subtle sine pop (Info/Warning)
            createOsc(880, 'sine', now, 0.1, 0.05);
        }
    } catch (e) {
        // Silent fail if audio blocked
    }
};

// 🎨 Glassmorphic Toast Component
const GlassToast = ({ t, type, message }: { t: Toast; type: 'success' | 'error' | 'warning' | 'info' | 'loading'; message: string }) => {
    const isVisible = t.visible;

    const config = {
        success: { icon: Check, bg: 'bg-emerald-500/20', border: 'border-emerald-500/20', text: 'text-emerald-100', iconColor: 'text-emerald-400' },
        error: { icon: AlertCircle, bg: 'bg-red-500/20', border: 'border-red-500/20', text: 'text-red-100', iconColor: 'text-red-400' },
        warning: { icon: AlertTriangle, bg: 'bg-amber-500/20', border: 'border-amber-500/20', text: 'text-amber-100', iconColor: 'text-amber-400' },
        info: { icon: Info, bg: 'bg-blue-500/20', border: 'border-blue-500/20', text: 'text-blue-100', iconColor: 'text-blue-400' },
        loading: { icon: Loader2, bg: 'bg-indigo-500/20', border: 'border-indigo-500/20', text: 'text-indigo-100', iconColor: 'text-indigo-400' },
    };

    const style = config[type];
    const Icon = style.icon;

    return (
        <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : -20, scale: isVisible ? 1 : 0.9 }}
            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
            className={`${isVisible ? 'pointer-events-auto' : 'pointer-events-none'} flex items-center gap-4 px-5 py-4 min-w-[320px] max-w-md rounded-[1.5rem] shadow-2xl backdrop-blur-xl border ${style.bg} ${style.border}`}
            style={{
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
            }}
        >
            <div className={`p-2 rounded-full ${style.bg} ${style.iconColor} shadow-inner`}>
                <Icon size={20} className={type === 'loading' ? 'animate-spin' : ''} strokeWidth={2.5} />
            </div>
            <div className="flex-1">
                <p className={`text-sm font-semibold tracking-wide ${style.text}`}>
                    {message}
                </p>
            </div>
            {type !== 'loading' && (
                <button
                    onClick={() => toast.dismiss(t.id)}
                    className="p-1 rounded-full hover:bg-white/10 text-white/40 hover:text-white/80 transition-colors"
                >
                    <X size={14} />
                </button>
            )}
        </motion.div>
    );
};

export const notify = {
    undo: (message: string, onUndo: () => void) => toast.custom((t) => (
        <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: t.visible ? 1 : 0, y: t.visible ? 0 : 50 }}
            className="flex items-center gap-4 px-6 py-4 bg-[#1D1D1F]/90 backdrop-blur-xl text-white rounded-[2rem] shadow-2xl border border-white/10 mb-8"
        >
            <span className="font-medium text-sm">{message}</span>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onUndo();
                    toast.dismiss(t.id);
                }}
                className="px-4 py-2 bg-brand-terracotta/90 hover:bg-brand-terracotta rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-lg active:scale-95"
            >
                Geri Al
            </button>
        </motion.div>
    ), { duration: 5000, position: 'bottom-center' }),

    success: (message: string) => {
        playToastSound('success');
        return toast.custom((t) => <GlassToast t={t} type="success" message={message} />, { duration: 3000, position: 'top-right' });
    },

    error: (message: string) => {
        playToastSound('error');
        return toast.custom((t) => <GlassToast t={t} type="error" message={message} />, { duration: 5000, position: 'top-right' });
    },

    warning: (message: string) => {
        playToastSound('warning');
        return toast.custom((t) => <GlassToast t={t} type="warning" message={message} />, { duration: 4000, position: 'top-right' });
    },

    info: (message: string) => {
        playToastSound('info');
        return toast.custom((t) => <GlassToast t={t} type="info" message={message} />, { duration: 3000, position: 'top-right' });
    },

    loading: (message: string) => {
        return toast.custom((t) => <GlassToast t={t} type="loading" message={message} />, { duration: Infinity, position: 'top-right' });
    },

    dismiss: (toastId?: string) => toast.dismiss(toastId),

    promise: async <T,>(
        promise: Promise<T>,
        messages: {
            loading: string;
            success: string | ((data: T) => string);
            error: string | ((err: any) => string);
        }
    ) => {
        // Use standard promise logic but map to custom generic renderers if needed, 
        // or just use manual handling for maximum control.
        // For simplicity with custom UI, we'll manually handle the promise states.

        const loadingId = notify.loading(messages.loading);

        try {
            const data = await promise;
            notify.dismiss(loadingId);
            notify.success(typeof messages.success === 'function' ? messages.success(data) : messages.success);
            return data;
        } catch (err: any) {
            notify.dismiss(loadingId);
            notify.error(typeof messages.error === 'function' ? messages.error(err) : messages.error);
            throw err;
        }
    }
};

export default notify;
